/**
 * Pure tree + fuzzy-filter logic for the EntityPicker — the searchable picker
 * that replaces the composer's flat `<select>` lists. Elements are dotted
 * qualified paths (`Platform.Auth.Login`), so a containment TREE is derivable by
 * splitting on `.` — no extra model data. Types / aspect keys are flat LISTS
 * that only need the fuzzy filter. All logic here is DOM-free and unit-tested;
 * the component is just chrome over these functions.
 */

export interface TreeNode {
  /** Full dotted path (`Platform.Auth`). */
  readonly path: string;
  /** Last segment (`Auth`). */
  readonly label: string;
  /** 0-based nesting depth. */
  readonly depth: number;
  /** True when this exact path is a pickable item (vs a synthesized ancestor). */
  readonly selectable: boolean;
  readonly children: TreeNode[];
}

/** Build a containment tree from dotted item paths. Intermediate segments that
 *  aren't themselves items become non-selectable ancestor nodes. */
export function buildTree(items: readonly string[]): TreeNode[] {
  const set = new Set(items);
  const roots: TreeNode[] = [];
  const byPath = new Map<string, TreeNode>();
  for (const item of [...items].sort()) {
    const segs = item.split(".");
    let siblings = roots;
    let prefix = "";
    for (let i = 0; i < segs.length; i++) {
      prefix = prefix ? `${prefix}.${segs[i]}` : segs[i]!;
      let node = byPath.get(prefix);
      if (!node) {
        node = { path: prefix, label: segs[i]!, depth: i, selectable: set.has(prefix), children: [] };
        byPath.set(prefix, node);
        siblings.push(node);
      }
      siblings = node.children;
    }
  }
  return roots;
}

/** Every path that has descendants — the set the tree can expand at. */
export function expandablePaths(nodes: readonly TreeNode[]): Set<string> {
  const out = new Set<string>();
  const walk = (ns: readonly TreeNode[]): void => {
    for (const n of ns) {
      if (n.children.length > 0) {
        out.add(n.path);
        walk(n.children);
      }
    }
  };
  walk(nodes);
  return out;
}

/** Pre-order flatten of the nodes visible under a given expanded set — the row
 *  order the keyboard arrows move through. */
export function flattenVisible(nodes: readonly TreeNode[], expanded: ReadonlySet<string>): TreeNode[] {
  const out: TreeNode[] = [];
  const walk = (ns: readonly TreeNode[]): void => {
    for (const n of ns) {
      out.push(n);
      if (n.children.length > 0 && expanded.has(n.path)) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

/** Score one candidate against a lowercased query: substring (best, ranked by
 *  position + tightness) then subsequence (worse) then -1 (no match). */
export function scoreMatch(hay: string, needle: string): number {
  const idx = hay.indexOf(needle);
  if (idx >= 0) return idx * 2 + (hay.length - needle.length) * 0.01;
  let from = 0;
  let gaps = 0;
  let last = -1;
  let started = false;
  for (const ch of needle) {
    const found = hay.indexOf(ch, from);
    if (found < 0) return -1;
    if (started) gaps += found - last - 1;
    from = found + 1;
    last = found;
    started = true;
  }
  return 500 + gaps;
}

/** Rank items by fuzzy match to `query` (empty query → all, original order). */
export function filterItems(items: readonly string[], query: string): string[] {
  const q = query.trim().toLowerCase();
  if (!q) return [...items];
  const scored: { p: string; s: number }[] = [];
  for (const p of items) {
    const s = scoreMatch(p.toLowerCase(), q);
    if (s >= 0) scored.push({ p, s });
  }
  scored.sort((a, b) => a.s - b.s || a.p.localeCompare(b.p));
  return scored.map((x) => x.p);
}

/** Ancestor paths of an item (`Platform.Auth.Login` → Platform, Platform.Auth)
 *  — used to auto-expand the tree down to the current value on open. */
export function ancestorsOf(path: string): string[] {
  const segs = path.split(".");
  const out: string[] = [];
  let prefix = "";
  for (let i = 0; i < segs.length - 1; i++) {
    prefix = prefix ? `${prefix}.${segs[i]}` : segs[i]!;
    out.push(prefix);
  }
  return out;
}
